import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { ArrowLeft, LogIn } from 'lucide-react';
import {
  getStaffTenant,
  staffSetPlan,
  staffSetStatus,
  staffGrant,
  staffImpersonate,
} from '../../api/staff';
import { useAuthStore } from '../../store/auth';
import { useT, type DictKey } from '../../i18n';
import { formatRubMinor, formatDateShort } from '../../lib/staffFormat';
import { StatusBadge } from './StaffTenantsPage';

const PLAN_KEY: Record<string, DictKey> = {
  free: 'billing.plan.free',
  pro: 'billing.plan.pro',
  business: 'billing.plan.business',
  enterprise: 'billing.plan.enterprise',
};
const PAID_PLANS = ['pro', 'business', 'enterprise'] as const;
const ALL_PLANS = ['free', ...PAID_PLANS] as const;

// ANX-087b/c/e — карточка клиента: данные, операторы, счета, действия,
// impersonation.
export function StaffTenantDetail({ id }: { id: string }) {
  const t = useT();
  const qc = useQueryClient();
  const [err, setErr] = useState<string | null>(null);
  const q = useQuery({ queryKey: ['staff_tenant', id], queryFn: () => getStaffTenant(id) });

  function invalidate() {
    qc.invalidateQueries({ queryKey: ['staff_tenant', id] });
    qc.invalidateQueries({ queryKey: ['staff_tenants'] });
    qc.invalidateQueries({ queryKey: ['staff_metrics'] });
  }
  const onErr = () => setErr(t('staff.actionError'));

  const planMu = useMutation({ mutationFn: (p: string) => staffSetPlan(id, p), onSuccess: () => { setErr(null); invalidate(); }, onError: onErr });
  const statusMu = useMutation({ mutationFn: (s: string) => staffSetStatus(id, s), onSuccess: () => { setErr(null); invalidate(); }, onError: onErr });
  const grantMu = useMutation({ mutationFn: (v: { plan: string; days: number }) => staffGrant(id, v.plan, v.days), onSuccess: () => { setErr(null); invalidate(); }, onError: onErr });

  const [planSel, setPlanSel] = useState('pro');
  const [grantPlan, setGrantPlan] = useState('pro');
  const [grantDays, setGrantDays] = useState(14);

  async function impersonate() {
    setErr(null);
    try {
      const res = await staffImpersonate(id);
      // Кладём operator-сессию в authStore и открываем панель в новой вкладке.
      useAuthStore.getState().setSession({
        access: res.access,
        refresh: '',
        accessExpiresAt: Date.now() + res.expires_in * 1000,
        operator: {
          id: res.operator.id,
          tenantId: res.operator.tenant_id,
          email: '',
          name: t('staff.impersonating'),
          role: 'admin',
        },
      });
      window.open('/', '_blank', 'noopener');
    } catch {
      onErr();
    }
  }

  if (q.isLoading) return <p className="text-sm text-slate-500">{t('common.loading')}</p>;
  if (q.isError || !q.data) return <p className="text-sm text-red-600">{t('staff.loadError')}</p>;
  const { tenant, operators, invoices } = q.data;
  const busy = planMu.isPending || statusMu.isPending || grantMu.isPending;
  const field = 'rounded border border-slate-300 px-2 py-1 text-sm focus:border-accent-500 focus:ring-accent-500';

  return (
    <div className="space-y-6">
      <Link to="/staff/tenants" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-900">
        <ArrowLeft size={15} aria-hidden="true" />
        {t('staff.detail.back')}
      </Link>

      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold text-slate-900">{tenant.name}</h1>
          <div className="mt-1 flex items-center gap-2 text-sm text-slate-500">
            <span>{tenant.slug}</span>
            <StatusBadge status={tenant.status} />
            <span>· {t(PLAN_KEY[tenant.plan] ?? 'billing.plan.free')}</span>
          </div>
        </div>
        <button
          type="button"
          onClick={impersonate}
          className="inline-flex items-center gap-1.5 rounded-md border border-slate-300 px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50"
        >
          <LogIn size={15} aria-hidden="true" />
          {t('staff.detail.impersonate')}
        </button>
      </div>

      {err && <p role="alert" className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{err}</p>}

      <div className="grid gap-4 sm:grid-cols-3">
        <Info label={t('staff.col.seats')} value={String(tenant.seats_used)} />
        <Info label={t('staff.col.dialogsMonth')} value={String(tenant.dialogs_month)} />
        <Info label={t('staff.detail.subscription')} value={tenant.sub_status ? `${tenant.sub_status} · ${formatDateShort(tenant.current_period_end)}` : '—'} />
      </div>

      {/* Действия */}
      <section className="space-y-4 rounded-md border border-slate-200 bg-white p-5 shadow-card">
        <h2 className="text-sm font-medium text-slate-900">{t('staff.detail.actions')}</h2>

        <div className="flex flex-wrap items-end gap-2">
          <label className="text-sm text-slate-700">
            <span className="block text-xs text-slate-500">{t('staff.detail.changePlan')}</span>
            <select value={planSel} onChange={(e) => setPlanSel(e.target.value)} className={field + ' mt-1 w-40'}>
              {ALL_PLANS.map((p) => <option key={p} value={p}>{t(PLAN_KEY[p])}</option>)}
            </select>
          </label>
          <button type="button" disabled={busy} onClick={() => planMu.mutate(planSel)} className="rounded-md bg-accent-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-accent-700 disabled:opacity-50">{t('staff.detail.apply')}</button>
        </div>

        <div className="flex flex-wrap items-end gap-2">
          <label className="text-sm text-slate-700">
            <span className="block text-xs text-slate-500">{t('staff.detail.grant')}</span>
            <select value={grantPlan} onChange={(e) => setGrantPlan(e.target.value)} className={field + ' mt-1 w-40'}>
              {PAID_PLANS.map((p) => <option key={p} value={p}>{t(PLAN_KEY[p])}</option>)}
            </select>
          </label>
          <label className="text-sm text-slate-700">
            <span className="block text-xs text-slate-500">{t('staff.detail.days')}</span>
            <input type="number" min={1} max={366} value={grantDays} onChange={(e) => setGrantDays(Number(e.target.value))} className={field + ' mt-1 w-24'} />
          </label>
          <button type="button" disabled={busy} onClick={() => grantMu.mutate({ plan: grantPlan, days: grantDays })} className="rounded-md border border-accent-600 px-3 py-1.5 text-sm font-medium text-accent-700 hover:bg-accent-50 disabled:opacity-50">{t('staff.detail.grantBtn')}</button>
        </div>

        <div className="flex flex-wrap gap-2 pt-1">
          {tenant.status !== 'active' && (
            <button type="button" disabled={busy} onClick={() => statusMu.mutate('active')} className="rounded-md border border-emerald-300 px-3 py-1.5 text-sm font-medium text-emerald-700 hover:bg-emerald-50 disabled:opacity-50">{t('staff.detail.activate')}</button>
          )}
          {tenant.status === 'active' && (
            <button type="button" disabled={busy} onClick={() => statusMu.mutate('suspended')} className="rounded-md border border-amber-300 px-3 py-1.5 text-sm font-medium text-amber-700 hover:bg-amber-50 disabled:opacity-50">{t('staff.detail.suspend')}</button>
          )}
          <button
            type="button"
            disabled={busy}
            onClick={() => { if (window.confirm(t('staff.detail.deleteConfirm'))) statusMu.mutate('deleted'); }}
            className="rounded-md border border-red-300 px-3 py-1.5 text-sm font-medium text-red-700 hover:bg-red-50 disabled:opacity-50"
          >
            {t('staff.detail.delete')}
          </button>
        </div>
      </section>

      {/* Операторы */}
      <section className="rounded-md border border-slate-200 bg-white p-5 shadow-card">
        <h2 className="mb-3 text-sm font-medium text-slate-900">{t('staff.detail.operators')} · {operators.length}</h2>
        <table className="w-full text-sm">
          <tbody className="divide-y divide-slate-100">
            {operators.map((o) => (
              <tr key={o.id}>
                <td className="py-1.5 text-slate-900">{o.email}</td>
                <td className="py-1.5 text-slate-500">{o.role}</td>
                <td className="py-1.5 text-right"><StatusBadge status={o.status === 'active' ? 'active' : 'suspended'} /></td>
              </tr>
            ))}
            {operators.length === 0 && <tr><td className="py-2 text-sm text-slate-400">{t('staff.detail.noOperators')}</td></tr>}
          </tbody>
        </table>
      </section>

      {/* Счета */}
      {invoices.length > 0 && (
        <section className="rounded-md border border-slate-200 bg-white p-5 shadow-card">
          <h2 className="mb-3 text-sm font-medium text-slate-900">{t('staff.detail.invoices')}</h2>
          <table className="w-full text-sm">
            <tbody className="divide-y divide-slate-100">
              {invoices.map((i) => (
                <tr key={i.id}>
                  <td className="py-1.5 font-medium text-slate-900">{`№ ${i.number}`}</td>
                  <td className="py-1.5 text-slate-500">{t(PLAN_KEY[i.plan] ?? 'billing.plan.free')}</td>
                  <td className="py-1.5 text-right">{formatRubMinor(i.amount_minor)}</td>
                  <td className="py-1.5 text-right text-slate-500">{i.status}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}
    </div>
  );
}

function Info({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-md border border-slate-200 bg-white px-4 py-3 shadow-card">
      <div className="text-xs uppercase tracking-wide text-slate-500">{label}</div>
      <div className="mt-0.5 text-sm font-medium text-slate-900">{value}</div>
    </div>
  );
}
